<template>
  <div>
    <crud-table
      ref="table"
      :entity="entity"
      :columns="columns"
      :custom-actions="customActions"
      :allow-create="allowCreate"
      :allow-delete="allowDelete"
      :allow-edit="allowEdit"
      :allow-actions="allowActions && !disabled"
      :disable-filtering="disableFiltering"
      :disable-sorting="disableSorting"
      :loading="loading"
      :on-row-dbl-click="onTableRowDoubleClickComputed"
      :on-create="onCreate"
      :on-edit="onEdit"
      :permission-code="permissionCode"
      :filters="parentIdentifierObject"
      :generate-summary="generateSummary"
      :on-open-context-menu="onOpenContextMenu"
      :on-context-menu-option-clicked="onContextMenuOptionClicked"
      :persist-state="persistState"
      :show-summary-row="showSummaryRow"
      :get-summaries="getSummaries"
      :use-pagination="usePagination"
      :show-action-icons-in-list="showActionIconsInList"
      is-embedded
      @create="showCreateComputed"
      @edit="showEditComputed"
    />
    <dialog-editor
      v-if="editorIsPopup"
      ref="dialog"
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      :definition="definition"
      :save="saveData"
      :page-loading="loading"
      :force-values="parentIdentifierObject"
      :tabbed="tabbed"
      v-bind="editorProps"
      :layout="layout"
      @saved="handleSaved"
    />
  </div>
</template>
<script>
import CrudTable from '@/components/crud/CrudTable';
import DialogEditor from '@/components/crud/DialogEditor';
export default {
  components: {
    CrudTable,
    DialogEditor
  },
  props: {
    entity: {
      type: String,
      required: true
    },
    entityStore: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    customActions: {
      default() {
        return [];
      },
      type: Array
    },
    generateSummary: {
      type: Function,
      required: true,
      default: () => {}
    },
    getSummaries: {
      type: Function
    },
    usePagination: {
      type: Boolean,
      default: true
    },
    showActionIconsInList: {
      type: Boolean,
      default: true
    },
    onOpenContextMenu: {
      type: Function
    },
    onContextMenuOptionClicked: {
      type: Function
    },
    definition: {
      type: Object,
      required: true,
      default: () => {}
    },
    createDefinition: {
      type: Function,
      default: () => {}
    },
    editDefinition: {
      type: Function,
      default: () => {}
    },
    createItemFromFormData: {
      type: Function,
      default: () => {}
    },
    createFormDataFromItem: {
      type: Function,
      default: () => {}
    },
    // Used for request filtering and forced form value
    // eg. parent of RentalAgreement is clientID
    parentIdFilterField: {
      type: String,
      required: false
    },
    parentIdentifiers: {
      type: Object,
      default: () => null
    },
    permissionCode: String,
    caption: String,
    disabled: {
      default: false,
      type: Boolean
    },
    openInFullscreen: {
      default: false,
      type: Boolean
    },
    required: {
      default: false,
      type: Boolean
    },
    disableFiltering: {
      default: false,
      type: Boolean
    },
    disableSorting: {
      default: false,
      type: Boolean
    },
    allowActions: {
      default: true,
      type: Boolean
    },
    allowDelete: {
      default: true,
      type: Boolean
    },
    allowEdit: {
      default: true,
      type: Boolean
    },
    editorNumber: Number,
    refreshOnFormSave: Boolean,
    persistState: {
      type: Boolean,
      default: false
    },
    showSummaryRow: {
      type: Boolean,
      default: false
    },
    tabbed: {
      type: Boolean,
      default: false
    },
    parentID: Number,
    editorIsPopup: {
      type: Boolean,
      default: true
    },
    onCreate: {
      type: Function,
      default: () => {}
    },
    onEdit: {
      type: Function,
      default: () => {}
    },
    onTableRowDoubleClick: Function,
    showCreate: Function,
    showEdit: Function,
    allowCreate: {
      default: true,
      type: Boolean
    }
  },
  data() {
    return {
      dialogVisible: false,
      clickedRowID: 0,
      editorProps: {},
      dialogTitle: '',
      layout: () => {
        return { groups: [] };
      }
    };
  },
  computed: {
    loading() {
      return this.$store.getters[this.entityStore + '/isLoading'];
    },
    internalParentID() {
      return this.parentID || (this.$route.params && +this.$route.params.id);
    },
    parentIdentifierObject() {
      if (this.parentIdentifiers) {
        return this.parentIdentifiers;
      }
      const obj = {};
      if (this.parentIdFilterField) {
        obj[this.parentIdFilterField] = this.internalParentID;
      }
      return obj;
    },
    showCreateComputed() {
      return this.editorIsPopup ? this.showCreateInternal : this.showCreate;
    },
    showEditComputed() {
      return this.editorIsPopup ? this.showEditInternal : this.showEdit;
    },
    onTableRowDoubleClickComputed() {
      if (this.onTableRowDoubleClick) {
        return this.onTableRowDoubleClick;
      }
      return this.editorIsPopup ? this.onTableRowDoubleClickInternal : null;
    }
  },
  mounted() {
    this.$root.$on('editor-saved', (editorNumber, form) => {
      if (this.refreshOnFormSave && editorNumber === this.editorNumber) {
        this.refresh();
      }
    });
  },
  methods: {
    showCreateInternal() {
      if (this.disabled) {
        return false;
      }
      if (this.createDefinition) {
        this.layout = this.createDefinition;
      }
      this.editorProps = { isEdit: false };
      this.dialogVisible = true;
      this.dialogTitle = this.$t('route.create' + this.entity);
      if (this.$refs.dialog) {
        this.$refs.dialog.clearForm();
      }
    },
    showEditInternal(id) {
      if (this.disabled) {
        return false;
      }
      if (this.editDefinition) {
        this.layout = this.editDefinition;
      }
      this.dialogTitle = this.$t('route.edit' + this.entity);
      this.editorProps = { id, isEdit: true, fetchById: this.fetchById };
      this.dialogVisible = true;
    },
    onTableRowDoubleClickInternal(row) {
      return this.showEditInternal(row.id);
    },
    handleSaved() {
      this.dialogVisible = false;
      this.refresh();
    },
    saveData(form) {
      const item = this.createItemFromFormData(this, form);
      return this.$store.dispatch(this.entityStore + '/persistItem', item);
    },
    fetchById(id) {
      return this.$store
        .dispatch(this.entityStore + '/getItem', { selector: { id }, options: { dontSave: true } })
        .then(item => this.createFormDataFromItem(this, item));
    },
    refresh() {
      if (this.$refs.table) {
        this.$refs.table.refreshList();
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
